import { useEffect, useRef } from 'react';

/**
 * useWebSocket — connects to the backend WebSocket and calls onMessage on every scan event.
 * Automatically reconnects on disconnect.
 *
 * Uses a ref for onMessage so the consumer can pass a fresh inline callback every render
 * without tearing down and reopening the WebSocket.
 */
export default function useWebSocket(onMessage) {
  const wsRef         = useRef(null);
  const reconnectRef  = useRef(null);
  const unmountedRef  = useRef(false);
  const onMessageRef  = useRef(onMessage);

  // Keep the callback reference fresh on every render — does NOT trigger reconnect.
  useEffect(() => { onMessageRef.current = onMessage; });

  useEffect(() => {
    unmountedRef.current = false;

    const connect = () => {
      if (unmountedRef.current) return;

      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const url = `${protocol}://${window.location.host}/ws`;

      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          if (reconnectRef.current) {
            clearTimeout(reconnectRef.current);
            reconnectRef.current = null;
          }
        };
        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.type === 'scan' && onMessageRef.current) onMessageRef.current(data);
          } catch (_) {}
        };
        ws.onclose = () => {
          if (!unmountedRef.current) {
            reconnectRef.current = setTimeout(connect, 3000);
          }
        };
        ws.onerror = () => ws.close();
      } catch (_) {}
    };

    connect();

    return () => {
      unmountedRef.current = true;
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) {}
        wsRef.current = null;
      }
    };
    // Empty deps: WebSocket is set up once per mount; callback updates flow via ref.
  }, []); // eslint-disable-line
}
